import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea } from "recharts";
import { Loader2, Plus, Ruler, Scale, Trash2, Info } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const NAVY = "#E8E1D6";
const GREEN = "#0F6B6B";
const AMBER = "#D9774A";
const RED = "#A8372A";

const CHART_MARGIN = { top: 10, right: 8, left: -20, bottom: 0 };
const X_TICK_STYLE = { fill: "#6B635C", fontSize: 10, fontWeight: 600, letterSpacing: 1 };
const Y_TICK_STYLE = { fill: "#6B635C", fontSize: 10 };
const TOOLTIP_CURSOR = { stroke: "rgba(15,107,107,0.4)", strokeDasharray: "3 3" };

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

// Same colour language as PackBadge / reliability chips.
function bmiStyle(category) {
  switch (category) {
    case "healthy range":
      return { text: "text-[#0F6B6B]", pill: "bg-[#0F6B6B]/15 text-[#0F6B6B] border-[#0F6B6B]/30" };
    case "underweight":
    case "overweight":
      return { text: "text-[#7A5E18]", pill: "bg-[#F6ECC9] text-[#7A5E18] border-[#ECDCA6]" };
    case "obese":
      return { text: "text-[#A8372A]", pill: "bg-[#F9E4DF] text-[#A8372A] border-[#F0C9C1]" };
    default:
      return { text: "text-[#6B635C]", pill: "bg-[#EEE9E0] text-[#6B635C] border-[#C9BFAF]/30" };
  }
}

function LineTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-[#E8E1D6] bg-[#FAF7F2] px-3 py-2 shadow-lg" data-testid="weight-tooltip">
      <div className="text-[10px] uppercase tracking-widest text-[#6B635C]">{label}</div>
      <div className="text-sm font-bold text-[#0F6B6B]">{payload[0].value.toFixed(1)} kg</div>
    </div>
  );
}

export default function ProgressTab({ client }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weightInput, setWeightInput] = useState("");
  const [heightInput, setHeightInput] = useState("");
  const [savingWeight, setSavingWeight] = useState(false);
  const [savingHeight, setSavingHeight] = useState(false);
  const [boxSize, setBoxSize] = useState({ w: 0, h: 0 });
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${client.client_id}/metrics`);
      setMetrics(data);
      setHeightInput(data.height_cm == null ? "" : String(data.height_cm));
    } catch {
      setMetrics({ height_cm: null, current_weight_kg: null, current_bmi: null, current_bmi_category: null, history: [] });
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!boxRef.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0 && rect.height > 0) {
        setBoxSize({ w: rect.width, h: rect.height });
      }
    });
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, [metrics]);

  const chartData = useMemo(() => {
    if (!metrics?.history) return [];
    return metrics.history.map((h) => ({
      label: new Date(h.recorded_at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" }),
      recorded_at: h.recorded_at,
      metric_id: h.metric_id,
      value: h.weight_kg,
    }));
  }, [metrics]);

  const addWeight = async () => {
    const val = Number(weightInput);
    if (!val || val < 10 || val > 500) {
      toast.error("Weight must be between 10 and 500 kg");
      return;
    }
    setSavingWeight(true);
    try {
      await api.post(`/clients/${client.client_id}/weights`, { weight_kg: val });
      setWeightInput("");
      await load();
      toast.success("Weight logged");
    } catch {
      toast.error("Could not save weight");
    } finally {
      setSavingWeight(false);
    }
  };

  const saveHeight = async () => {
    const val = heightInput === "" ? null : Number(heightInput);
    if (val !== null && (val < 50 || val > 260)) {
      toast.error("Height must be between 50 and 260 cm");
      return;
    }
    setSavingHeight(true);
    try {
      await api.put(`/clients/${client.client_id}/height`, { height_cm: val });
      await load();
      toast.success("Height saved");
    } catch {
      toast.error("Could not save height");
    } finally {
      setSavingHeight(false);
    }
  };

  const removeEntry = async (metric_id) => {
    try {
      await api.delete(`/clients/${client.client_id}/weights/${metric_id}`);
      await load();
      toast.success("Entry removed");
    } catch {
      toast.error("Could not delete entry");
    }
  };

  if (loading && !metrics) {
    return (
      <div className="flex items-center gap-2 text-[#6B635C] py-12 justify-center" data-testid="progress-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading progress…
      </div>
    );
  }
  if (!metrics) return null;

  const { current_weight_kg, current_bmi, current_bmi_category, height_cm, history } = metrics;
  const bmiCol = bmiStyle(current_bmi_category);

  return (
    <div className="space-y-6" data-testid="progress-tab">
      {/* Headline row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3" data-testid="progress-current-weight">
          <p className="text-[10px] uppercase tracking-widest text-[#6B635C]">Current weight</p>
          <p className="font-display text-2xl font-bold mt-1">
            {current_weight_kg == null ? "—" : `${current_weight_kg.toFixed(1)} kg`}
          </p>
          <p className="text-[10px] text-[#6B635C]">
            {history.length > 0 ? `logged ${fmtDate(history[history.length - 1].recorded_at)}` : "no entries yet"}
          </p>
        </div>
        <div className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3" data-testid="progress-current-bmi">
          <p className="text-[10px] uppercase tracking-widest text-[#6B635C]">BMI</p>
          <p className={`font-display text-2xl font-bold mt-1 ${bmiCol.text}`}>
            {current_bmi == null ? "—" : (
              <>
                {current_bmi}
                <span className="text-xs font-medium text-[#2A2623] ml-2" data-testid="progress-bmi-category">
                  , {current_bmi_category}
                </span>
              </>
            )}
          </p>
          <p className="text-[10px] text-[#6B635C] leading-tight mt-1 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              BMI doesn't account for muscle mass — one data point, not a full measure of health.
            </span>
          </p>
        </div>
      </div>

      {/* Weight history chart */}
      <div>
        <p className="tm-label mb-2">Weight history</p>
        <div className="tm-card p-3 sm:p-4">
          {history.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-center" data-testid="progress-history-empty">
              <p className="text-[#6B635C] text-sm">No entries yet. Log the first one below.</p>
            </div>
          ) : (
            <div className="h-40 min-h-[160px] min-w-[240px]" ref={boxRef} data-testid="progress-chart-canvas">
              {boxSize.w > 0 && boxSize.h > 0 && (
                <LineChart width={boxSize.w} height={boxSize.h} data={chartData} margin={CHART_MARGIN}>
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={X_TICK_STYLE} minTickGap={16} />
                  <YAxis axisLine={false} tickLine={false} tick={Y_TICK_STYLE} width={40} domain={["auto", "auto"]} />
                  <Tooltip cursor={TOOLTIP_CURSOR} content={<LineTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={GREEN}
                    strokeWidth={2}
                    dot={{ fill: GREEN, r: 3, strokeWidth: 0 }}
                    activeDot={{ r: 5, fill: GREEN, stroke: NAVY, strokeWidth: 2 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Log new weight */}
      <div className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3" data-testid="progress-add-weight">
        <p className="text-[10px] uppercase tracking-widest text-[#6B635C] mb-2 flex items-center gap-1">
          <Scale className="w-3 h-3" /> Log new weight (kg)
        </p>
        <div className="flex gap-2">
          <Input
            type="number"
            min="10"
            max="500"
            step="0.1"
            inputMode="decimal"
            value={weightInput}
            onChange={(e) => setWeightInput(e.target.value)}
            data-testid="progress-weight-input"
            placeholder="72.5"
            className="bg-[#FAF7F2] border-[#E8E1D6]"
            onKeyDown={(e) => { if (e.key === "Enter") addWeight(); }}
          />
          <Button
            onClick={addWeight}
            disabled={savingWeight || !weightInput}
            data-testid="progress-weight-save"
            className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold disabled:opacity-40"
          >
            <Plus className="w-4 h-4" /> Log
          </Button>
        </div>
      </div>

      {/* Height */}
      <div className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3" data-testid="progress-height">
        <p className="text-[10px] uppercase tracking-widest text-[#6B635C] mb-2 flex items-center gap-1">
          <Ruler className="w-3 h-3" /> Height (cm) — set once
        </p>
        <div className="flex gap-2">
          <Input
            type="number"
            min="50"
            max="260"
            step="1"
            inputMode="decimal"
            value={heightInput}
            onChange={(e) => setHeightInput(e.target.value)}
            data-testid="progress-height-input"
            placeholder="Optional"
            className="bg-[#FAF7F2] border-[#E8E1D6]"
          />
          <Button
            onClick={saveHeight}
            disabled={savingHeight || heightInput === (height_cm == null ? "" : String(height_cm))}
            data-testid="progress-height-save"
            variant="outline"
            className="border-[#E8E1D6] bg-transparent hover:bg-[#FAF7F2] disabled:opacity-40"
          >
            Save
          </Button>
        </div>
      </div>

      {/* History table */}
      {history.length > 0 && (
        <div>
          <p className="tm-label mb-2">Entries ({history.length})</p>
          <ul className="rounded-md border border-[#E8E1D6] divide-y divide-[#E8E1D6]" data-testid="progress-history-list">
            {[...history].reverse().map((h) => (
              <li
                key={h.metric_id}
                className="flex items-center justify-between gap-2 px-3 py-2 bg-[#FFFFFF]/40"
                data-testid={`progress-history-row-${h.metric_id}`}
              >
                <div>
                  <p className="text-sm font-medium">{h.weight_kg.toFixed(1)} kg</p>
                  <p className="text-[10px] text-[#6B635C]">{fmtDate(h.recorded_at)}{h.notes ? ` · ${h.notes}` : ""}</p>
                </div>
                <button
                  onClick={() => removeEntry(h.metric_id)}
                  data-testid={`progress-history-delete-${h.metric_id}`}
                  className="text-[#6B635C] hover:text-[#A8372A] p-1"
                  title="Remove this entry"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
